import { Box, FormControl, Grid, MenuItem, Select, Typography, Card, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { HttpRequest } from './HttpRequest';
import { Line } from 'react-chartjs-2';
import { Visibility, Inventory2Outlined, ShoppingCartOutlined, PeopleAltOutlined, TrendingUpOutlined, BarChartOutlined, ReceiptLongOutlined } from '@mui/icons-material';
import CustomTable from './common/CustomTable';
import CurrencyFormatter from './common/currencyFormatter';
import { useNavigate } from 'react-router-dom';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend} from 'chart.js';
ChartJS.register( CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);


const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [totalProducts, setTotalProducts] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalSales, setTotalSales] = useState(0);
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [salesData, setSalesData] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);

  // Generate year options from 2022 to current year
  const yearOptions = Array.from({ length: currentYear - 2021 }, (_, i) => 2022 + i);

  const formatDate = (value) => {
    const dateString = value;
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
  };

  const columns = [
    {
      field: 'orderNumber', headerName: 'order Number', flex: 1,
      renderHeader: (params) => { return (<Box style={{ color: "#032541", fontSize: isMobile ? "12px" : "14px", fontWeight: "700" }}> Order Number  </Box>) },
      renderCell: (params) => { return (<Box style={{ cursor: 'pointer', color: "#707070", fontSize: isMobile ? "11px" : "14px", fontWeight: "500" }}> {params.row.orderNumber}  </Box>) }
    },
    {
      field: 'createdAt', headerName: 'Date', flex: 1,
      renderHeader: (params) => { return (<Box style={{ color: "#032541", fontSize: isMobile ? "12px" : "14px", fontWeight: "700" }}> Date  </Box>) },
      renderCell: (params) => {
        return (<Box style={{ cursor: 'pointer', color: "#707070", fontSize: isMobile ? "11px" : "14px", fontWeight: "500" }}>
          {formatDate(params.row.createdAt)}
        </Box>)
      }
    },
    {
      field: 'customerName', headerName: 'Customer Name', flex: 1.2,
      renderHeader: (params) => { return (<Box style={{ color: "#032541", fontSize: isMobile ? "12px" : "14px", fontWeight: "700" }}> Customer Name  </Box>) },
      renderCell: (params) => { return (<Box style={{ cursor: 'pointer', color: "#707070", fontSize: isMobile ? "11px" : "14px", fontWeight: "500" }}>    {params.row.customerName}  </Box>) }
    },
    {
      field: 'customerAddress', headerName: 'Delivery Address', flex: 1.1,
      renderHeader: (params) => { return (<Box style={{ color: "#032541", fontSize: isMobile ? "12px" : "14px", fontWeight: "700" }}> Delivery Address  </Box>) },
      renderCell: (params) => { return (<Box  style={{ cursor: 'pointer', color: "#707070", fontSize: isMobile ? "11px" : "14px", fontWeight: "500" }}>  
        {typeof params?.row?.customerAddress === 'object' && params?.row?.customerAddress !== null ? (
          <>
            { params?.row?.customerAddress?.address && <div>{ params?.row?.customerAddress?.address}</div>}
            { params?.row?.customerAddress?.apartment && <div>{ params?.row?.customerAddress?.apartment}</div>}
            { params?.row?.customerAddress?.city && <div>{ params?.row?.customerAddress?.city}</div>}
          </>
        ) : (
         params?.row?.customerAddress
        )}
         </Box>) }
    },
    {
      field: 'amount', headerName: 'Amount', flex: 0.8,
      renderHeader: (params) => { return (<Box style={{ color: "#032541", fontSize: isMobile ? "12px" : "14px", fontWeight: "700" }}> Amount  </Box>) },
      renderCell: (params) => {
        return (<Box style={{ cursor: 'pointer', color: "#707070", fontSize: isMobile ? "11px" : "14px", fontWeight: "500" }}>
          {CurrencyFormatter(params.row.amount, 'KES')}
        </Box>)
      }
    },
    {
      field: 'paymentStatus', headerName: 'Payment Status', flex: 1,
      renderHeader: (params) => { return (<Box style={{ color: "#032541", fontSize: isMobile ? "12px" : "14px", fontWeight: "700" }}> Payment Status  </Box>) },
      renderCell: (params) => {
        const statusColors = {'paid': '#17ae7b', 'unpaid': '#dc3545',};
        const color = statusColors[params.row.status] || '#dc3545';
        return (
          <Box sx={{ backgroundColor: `${color}20`, color: color, fontWeight: 600, px: 1, py: 0.1, borderRadius: '8px', border: `1px solid ${color}`, fontSize: isMobile ? "10px" : "12.5px", lineHeight: 1.2, whiteSpace: 'nowrap', display: 'inline-block', textTransform: 'capitalize'}}>
            {params.row.status}
          </Box>
        );
      }
    },
    {
      field: 'status', headerName: 'Order Status', flex: 1,
      renderHeader: (params) => { return (<Box style={{ color: "#032541", fontSize: isMobile ? "12px" : "14px", fontWeight: "700" }}> Order Status  </Box>) },
      renderCell: (params) => {
        const statusColors = { 'Received': '#707070', 'Delivered': '#17ae7b', 'Shipped': '#000000', 'Processing': '#f79009'};
        const color = statusColors[params.row?.orderStatus] || '#dc3545';
        return (
          <Box sx={{ backgroundColor: `${color}20`, color: color, fontWeight: 600, px: 1, py: 0.1, borderRadius: '8px', border: `1px solid ${color}`, fontSize: isMobile ? "10px" : "12.5px", lineHeight: 1.2, whiteSpace: 'nowrap', display: 'inline-block', textTransform: 'capitalize'}}>
            {params.row?.orderStatus ?? 'Pending'}
          </Box>
        );
      }
    },
    {
      field: 'action', headerName: 'Action', flex: 1,
      renderHeader: (params) => { return (<Box style={{ color: "#032541", fontSize: isMobile ? "14px" : "17px", fontWeight: "700" }}>   </Box>) },
      renderCell: (params) => {
        return (
          <Box sx={{ cursor: 'pointer', color: '#707070', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%', gap: 0.5 }}>
            <Visibility sx={{ fontSize: isMobile ? "16px" : "20px", '&:hover': { color: '#032541' }}} onClick={() => { navigate(`/zed_ecommerce/orders/${params.row._id}/details`) }}/>
            <Typography sx={{ fontSize: isMobile ? "11px" : "14px", fontWeight: '500', '&:hover': { color: '#032541' }}} onClick={() => { navigate(`/zed_ecommerce/orders/${params.row._id}/details`) }}>
              Preview
            </Typography>
          </Box>
        );
      }
    },
  ];

  const fetchStats = async () => {
    HttpRequest({
      method: 'GET',
      url: `/api/v1/ecommerce/zed_ecommerce_summary`,
      body: null,
    }).then((data) => {
      if (data.status === 200) {
        setTotalCustomers(data.response.totalCustomers);
        setTotalOrders(data.response.totalOrders);
        setTotalSales(data.response.totalRevenue);
        setTotalProducts(data.response.totalproducts);
      }
    }).catch((error) => {
      
    });
  };

  const salesActivity = async () => {
    HttpRequest({
      method: 'GET',
      url: `/api/v1/ecommerce/zed_ecommerce_sales_activity?year=${year}`,
      body: null,
    }).then((data) => {
      if (data.status === 200) {
        const formattedData = formatSalesData(data.response.data);
        setSalesData(formattedData);
      }
    }).catch((error) => {
    });
  };

  const fetchOrders = async () => {
    HttpRequest({
      method: 'GET',
      url: `/api/v1/ecommerce/orders?page=1&limit=5`,
      body: null
    }).then((data) => {
      if (data.status === 200) {
        setTransactions(data.response.data);
      }
    }).catch((error) => {
    }).finally(() => {
      setTransactionsLoading(false);
    });
  };

  const salesActivityLabels = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const salesActvityData = {
    labels: salesActivityLabels,
    datasets: [
      {
        label: "Sales Activity",
        data: salesData,
        borderColor: "#032541",
        backgroundColor: "rgba(3, 37, 65, 0.1)",
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "#032541",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: isMobile ? 3 : 4,
        pointHoverRadius: isMobile ? 5 : 6,
      },
    ],
  };

  // Calculate dynamic max for y-axis
  const maxSales = Math.max(...salesData);
  const dynamicMax = maxSales > 0 ? maxSales * 1.15 : 1;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#032541',
        bodyColor: '#64748B',
        borderColor: '#e0e0e0',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: function(context) {
            return `Sales: Kshs ${context.parsed.y.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0,})}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#64748B',
          maxTicksLimit: isMobile ? 6 : 12,
          font: {
            size: isMobile ? 10 : 12
          }
        }
      },
      y: {
        min: 0,
        max: dynamicMax,
        grace: '5%',
        beginAtZero: true,
        ticks: {
          color: '#64748B',
          maxTicksLimit: salesData.every(v => v === 0) ? 3 : (isMobile ? 4 : 8),
          font: {
            size: isMobile ? 10 : 12
          },
          callback: function(value) {
            if (value >= 1_000_000_000) return 'Kshs ' + (value / 1_000_000_000).toFixed(0) + 'B';
            if (value >= 1_000_000) return 'Kshs ' + (value / 1_000_000).toFixed(0) + 'M';
            if (value >= 1_000) return 'Kshs ' + (value / 1_000).toFixed(0) + 'K';
            return 'Kshs ' + value.toFixed(0);
          }
        },
        grid: { color: 'rgba(0, 0, 0, 0.05)' }
      },
    },
    elements: {
      line: {
        borderWidth: isMobile ? 1.5 : 2,
      }
    }
  };

  const formatSalesData = (data) => {
    const formattedData = salesActivityLabels.map((month) => {
      const matchingData = data.find((item) => item.month === month);
      return matchingData ? matchingData.totalSales : 0;
    });
    return formattedData;
  };

  const handleYearChange = (event) => {
    setYear(event.target.value);
  };

  useEffect(() => {
    fetchStats();
    fetchOrders();
  }, []);
 
  useEffect(() => {
    salesActivity();
  }, [year]);

  const cardData = [
    {
      title: "Total Products",
      value: totalProducts.toLocaleString(),
      subtitle: "Active Items",
      color: "#5e0ac4",
      icon: Inventory2Outlined,
      onClick: () => navigate("/zed_ecommerce/products")
    },
    {
      title: "Total Orders",
      value: totalOrders.toLocaleString(),
      subtitle: "This Year",
      color: "#3da2f8",
      icon: ShoppingCartOutlined,
      onClick: () => navigate("/zed_ecommerce/orders")
    },
    {
      title: "Total Customers",
      value: totalCustomers.toLocaleString(),
      subtitle: "Registered Users",
      color: "#f79009",
      icon: PeopleAltOutlined,
      onClick: () => navigate("/zed_ecommerce/customers")
    },
    {
      title: "Total Sales",
      value: `Kshs ${totalSales.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
      subtitle: "Revenue This Year",
      color: "#17ae7b",
      icon: TrendingUpOutlined,
      onClick: () => navigate("/zed_ecommerce/reports")
    }
  ];

  return (
    <Box sx={{pt: 3, mt: 4, pb: 5, width: { xs: "100vw", sm: "calc(100vw - 360px)" }, maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box', px: { xs: 2, sm: 3 }, ml: { xs: -3, sm: 0} }}>      
      <Typography sx={{ fontSize: { xs: "18px", sm: "20px", md: "26px" }, fontWeight: 700, color: "#032541" }}>
        Dashboard
      </Typography>
      <Typography sx={{ fontSize: { xs: "11px", sm: "12px", md: "14px" }, color: "#64748B", mt: 0.5 }}>
        Financial overview and key performance metrics
      </Typography>

      {/* Cards Section */}
      <Grid container spacing={isMobile ? 1 : 2} mt={2}>
        {cardData.map((item) => (
          <Grid item xs={6} sm={6} md={3} key={item.title}>
            <Card onClick={item.onClick} sx={{ p: { xs: 1, sm: 2 }, height: '100%', cursor: 'pointer', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)' }, transition: 'all 0.3s ease'}}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Typography sx={{ fontWeight: 600, color: "#032541", fontSize: { xs: 11, sm: 14, md: 16 } }}>
                  {item.title}
                </Typography>
                <Box sx={{
                  backgroundColor: `${item.color}15`,
                  borderRadius: '12px',
                  p: { xs: 0.5, sm: 1 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <item.icon sx={{
                    fontSize: { xs: 18, sm: 24, md: 28 },
                    color: item.color
                  }} />
                </Box>
              </Box>

              <Typography sx={{ my: 1, fontSize: { xs: 14, sm: 18, md: 20 }, fontWeight: 700, color: item.color }}>
                {item.value}
              </Typography>

              <Typography sx={{ fontSize: { xs: 9, sm: 12 }, color: "#64748B" }}>{item.subtitle}</Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: { xs: 3, sm: 6 } }} />

      {/* Sales Activity Section */}
      <Box sx={{ p: { xs: 1.5, sm: 3 }, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)', borderRadius: 2, transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)' } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-start' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{
                backgroundColor: 'rgba(3, 37, 65, 0.1)',
                borderRadius: '12px',
                p: { xs: 0.8, sm: 1 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1.5
              }}>
                <BarChartOutlined sx={{ fontSize: { xs: 20, sm: 26 }, color: "#032541" }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: { xs: 13, sm: 16 }, color: "#032541" }}>
                  Sales Activity
                </Typography>
                <Typography sx={{ fontSize: { xs: 11, sm: 14 }, color: '#64748B', mt: 0.5 }}>
                  Monthly revenue performance overview
                </Typography>
              </Box>
            </Box>
            {isMobile && (
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select value={year} onChange={handleYearChange}>
                  {yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </Select>
              </FormControl>
            )}
          </Box>
          {!isMobile && (
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select value={year} onChange={handleYearChange}>
                {yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
          )}
        </Box>

        <Box sx={{ width: '100%', height: { xs: 180, sm: 250, md: 350 }, mt: 2 }}>
          <Line data={salesActvityData} options={chartOptions} />
        </Box>
      </Box>

      <Box sx={{ mt: { xs: 2, sm: 3 } }} />

      {/* Recent Orders Section */}
      <Box sx={{ p: { xs: 1.5, sm: 3 }, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)', borderRadius: 2, transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{
            backgroundColor: 'rgba(61, 162, 248, 0.1)',
            borderRadius: '12px',
            p: { xs: 0.8, sm: 1 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1.5
          }}>
            <ReceiptLongOutlined sx={{ fontSize: { xs: 20, sm: 26 }, color: '#3da2f8' }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: { xs: 13, sm: 16 }, color: "#032541" }}>
              Recent Orders
            </Typography>
            <Typography sx={{ fontSize: { xs: 11, sm: 14 }, color: '#64748B', mt: 0.5 }}>
              Latest customer orders and their status
            </Typography>
          </Box>
        </Box>

        <Box sx={{ width: '100%', mt: 2, overflowX: 'auto' }}>
          <Box sx={{ minWidth: { xs: 700, sm: '100%' } }}>
            <CustomTable columns={columns} data={transactions} 
              sx={{ border: "none",
                '& .MuiDataGrid-cell': { 
                  borderBottom: '1px solid #f0f0f0', 
                  py: isMobile ? 0.5 : 1,
                },
                '& .MuiDataGrid-columnHeaders': { 
                  backgroundColor: '#f8f9fa', 
                  borderBottom: '2px solid #e0e0e0',
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;